import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "relative justify-center cursor-pointer inline-flex items-center space-x-2 text-center font-normal ease-out duration-200 rounded-[var(--radius)] outline-none transition-colors outline-0 focus-visible:outline-4 focus-visible:outline-offset-1 border disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-']):not([class*='h-']):not([class*='w-'])]:size-3.5 shrink-0 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: `
          bg-primary text-primary-foreground
          hover:bg-primary/90
          border-primary
          focus-visible:outline-primary
          data-[state=open]:bg-primary/90
          data-[state=open]:outline-primary
        `,
        secondary: `
          text-foreground
          bg-muted dark:bg-muted
          hover:bg-accent
          border-border hover:border-muted-foreground
          focus-visible:outline-primary
          data-[state=open]:bg-accent
          data-[state=open]:outline-primary
        `,
        outline: `
          text-foreground
          bg-transparent
          border-border hover:border-muted-foreground
          focus-visible:outline-border
          data-[state=open]:border-foreground/30
          data-[state=open]:outline-border
        `,
        destructive: `
          bg-destructive text-destructive-foreground
          hover:bg-destructive/90
          border-destructive
          focus-visible:outline-destructive
          data-[state=open]:bg-destructive/90
          data-[state=open]:outline-destructive
        `,
        ghost: `
          text-foreground
          hover:bg-accent hover:text-accent-foreground
          border-transparent
          focus-visible:outline-border
          data-[state=open]:bg-accent
          data-[state=open]:outline-border
        `,
        link: `
          text-primary
          border-transparent
          hover:underline underline-offset-4
          focus-visible:outline-border
          data-[state=open]:outline-border
        `,
        glass: `
          text-foreground
          bg-primary/5 border-primary/20
          hover:bg-primary/10 hover:border-primary/30
          backdrop-blur-sm
          focus-visible:outline-primary
          data-[state=open]:bg-primary/15
          data-[state=open]:outline-primary
        `,
      },
      size: {
        default: "h-[26px] px-2.5 py-1 text-xs [&_svg:not([class*='size-']):not([class*='h-']):not([class*='w-'])]:size-3",
        sm: "h-[26px] px-2.5 py-1 text-xs [&_svg:not([class*='size-']):not([class*='h-']):not([class*='w-'])]:size-3",
        md: "h-[30px] px-3 py-1 text-xs [&_svg:not([class*='size-']):not([class*='h-']):not([class*='w-'])]:size-3.5",
        lg: "h-[34px] px-3.5 py-1.5 text-sm [&_svg:not([class*='size-']):not([class*='h-']):not([class*='w-'])]:size-3.5",
        icon: "size-[26px] p-0 [&_svg:not([class*='size-']):not([class*='h-']):not([class*='w-'])]:size-3",
        "icon-sm": "size-[26px] p-0 [&_svg:not([class*='size-']):not([class*='h-']):not([class*='w-'])]:size-3",
        "icon-md": "size-[30px] p-0 [&_svg:not([class*='size-']):not([class*='h-']):not([class*='w-'])]:size-3.5",
        "icon-lg": "size-[34px] p-0 [&_svg:not([class*='size-']):not([class*='h-']):not([class*='w-'])]:size-3.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  children,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        typeof children === "string" ? (
          <span className="truncate">{children}</span>
        ) : (
          children
        )
      )}
    </Comp>
  )
}

export { Button, buttonVariants }
